import {
  addMonths,
  getDaysInMonth,
  getMonth,
  getYear,
  startOfMonth,
  subMonths,
  format,
  startOfDay,
  addYears,
  startOfYear,
  subYears,
  isToday,
} from 'date-fns';
import React, {useState} from 'react';

import {
  Container,
  DateInputs,
  Input,
  CalendarHeader,
  CalendarToolButton,
  PrevIcon,
  NextIcon,
  Month,
  Table,
  TableHeader,
  HeaderRow,
  HeaderColumn,
  TableBody,
  Row,
  Column,
  Day,
  Empty,
  PrevYear,
  NextYear,
} from './styles';
import {ptBR} from 'date-fns/locale';
import {useTheme} from 'styled-components';

interface WeekDay {
  day?: number;
  weekDay: string;
}

interface Props {
  date?: Date;
  confirm: (date: Date) => void;
}

const DatePicker: React.FC<Props> = ({confirm, ...props}) => {
  const [currentMonth, setCurrentMonth] = useState(
    startOfMonth(props.date ?? new Date()),
  );
  const year = getYear(currentMonth);
  const previousMonth = subMonths(startOfMonth(currentMonth), 1);
  const nextMonth = addMonths(startOfMonth(currentMonth), 1);
  const month = getMonth(currentMonth);
  const quantityOfDays = getDaysInMonth(currentMonth);
  const {colors} = useTheme();

  const [date, setDate] = useState<Date | undefined>(props.date);

  const capitalizeFirstLetter = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const generateCalendarDays = () => {
    const calendar: WeekDay[] = [];
    for (let i = 1; i <= quantityOfDays; i++) {
      const date = new Date(year, month, i);
      calendar.push({
        day: i,
        weekDay: capitalizeFirstLetter(format(date, 'EEEEEE', {locale: ptBR})),
      });
    }

    return calendar;
  };

  const weekMockup = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

  const calendar = (): WeekDay[] => {
    const calendar = generateCalendarDays();
    const day1 = calendar[0];

    const insertFrom = weekMockup.indexOf(day1.weekDay);

    const calendarMockup = Array.from(
      Array(insertFrom < 5 ? 5 : 6),
      () => weekMockup,
    ).reduce((a, b) => [...a, ...b]);

    return calendarMockup.map((weekDay, index) => {
      if (index < insertFrom) return {weekDay};

      const isDate = calendar[index - insertFrom];

      if (isDate) return isDate;

      return {weekDay};
    });
  };

  function sliceIntoChunks(arr: WeekDay[], chunkSize: number) {
    const res = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      const chunk = arr.slice(i, i + chunkSize);
      res.push(chunk);
    }
    return res;
  }

  const changeDate = (day: number) => {
    const date = startOfDay(new Date(year, month, day));
    setDate(date);
    confirm(date);
  };

  const handleNextYear = () => {
    const now = startOfMonth(new Date());
    const currentYear = getYear(now);
    const nextYear = getYear(currentMonth) + 1;

    if (nextYear === currentYear) return setCurrentMonth(now);

    setCurrentMonth(startOfYear(addYears(currentMonth, 1)));
  };

  const handlePreviousYear = () => {
    const now = startOfMonth(new Date());
    const currentYear = getYear(now);
    const previousYear = getYear(currentMonth) - 1;

    if (previousYear === currentYear) return setCurrentMonth(now);

    setCurrentMonth(startOfYear(subYears(currentMonth, 1)));
  };

  return (
    <Container>
      <CalendarHeader>
        <CalendarToolButton onClick={handlePreviousYear}>
          <PrevYear />
        </CalendarToolButton>
        <CalendarToolButton onClick={() => setCurrentMonth(previousMonth)}>
          <PrevIcon />
        </CalendarToolButton>
        <Month>{format(currentMonth, 'MMMM yyyy', {locale: ptBR})}</Month>
        <CalendarToolButton onClick={() => setCurrentMonth(nextMonth)}>
          <NextIcon />
        </CalendarToolButton>
        <CalendarToolButton onClick={handleNextYear}>
          <NextYear />
        </CalendarToolButton>
      </CalendarHeader>
      <DateInputs>
        <Input>
          {date ? format(date, "LLL dd',' yyyy", {locale: ptBR}) : 'Data'}
        </Input>
      </DateInputs>
      <Table>
        <TableHeader>
          <HeaderRow>
            {weekMockup.map((weekDay, index) => (
              <HeaderColumn key={index}>{weekDay}</HeaderColumn>
            ))}
          </HeaderRow>
        </TableHeader>
        <TableBody>
          {sliceIntoChunks(calendar(), 7).map((week, index) => (
            <Row key={index}>
              {week.map(({day}, index) =>
                !day ? (
                  <Empty key={index} />
                ) : (
                  <Column
                    isDay={day}
                    active={date?.getDate() === day}
                    key={index}
                    onClick={() => changeDate(day)}>
                    <Day
                      style={{
                        backgroundColor: isToday(
                          startOfDay(new Date(year, month, day)),
                        )
                          ? 'rgba(242, 94, 55, .3)'
                          : undefined,
                      }}
                      active={date?.getDate() === day}>
                      {day}
                    </Day>
                  </Column>
                ),
              )}
            </Row>
          ))}
        </TableBody>
      </Table>
    </Container>
  );
};

export default DatePicker;
